/**
 * staging
 */
// import getConfig from "next/config";

// moving to new nextjs version
declare global {
  interface Window {
    __RUNTIME_CONFIG__: Record<string, string | undefined>;
  }
}
// Helper to get runtime configuration (Build once, Run anywhere)
const getRuntimeConfig = (key: string, defaultValue?: string): string | undefined => {
  const isServer = typeof window === "undefined";
  let val: string | undefined;

  if (!isServer) {
    val = window.__RUNTIME_CONFIG__?.[key];
    // console.debug(`[RuntimeConfig] Client-side fetch for ${key}:`, val);
  } else {
    val = process.env[key];
    // console.debug(`[RuntimeConfig] Server-side fetch for ${key}:`, val);
  }

  // Handle common pitfalls like "undefined" or "null" strings from some environments
  if (val === undefined || val === null || val === "" || val === "undefined" || val === "null") {
    return defaultValue;
  }

  return val;
};

const normalizeConfigValue = (value: string | null | undefined): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") {
    return undefined;
  }

  return trimmed;
};

const readConfigValue = (key: string): string | undefined => {
  return normalizeConfigValue(getRuntimeConfig(key) || process.env[key]);
};

const hasUrlScheme = (value: string): boolean => /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);

const normalizeUrlLikeValue = (value: string | undefined, defaultProto = "https"): string | undefined => {
  const normalizedValue = normalizeConfigValue(value);
  if (!normalizedValue) {
    return undefined;
  }

  if (normalizedValue === "http://undefined" || normalizedValue === "https://undefined") {
    return undefined;
  }

  if (hasUrlScheme(normalizedValue)) {
    try {
      const parsed = new URL(normalizedValue);
      if (!parsed.hostname || parsed.hostname === "undefined" || parsed.hostname === "null") {
        return undefined;
      }
      return normalizedValue;
    } catch {
      return undefined;
    }
  }

  return `${defaultProto}://${normalizedValue.replace(/^\/\//, "")}`;
};

const resolveSessionmgrUrl = (): string | undefined => {
  const explicitUrl = normalizeUrlLikeValue(
    readConfigValue("NEXT_PUBLIC_SESSIONMGR_URL") ||
      readConfigValue("NEXT_PUBLIC_sessionmgr_baseurl") ||
      readConfigValue("SESSIONMGR_URL") ||
      readConfigValue("sessionmgr_baseurl")
  );
  if (explicitUrl) {
    return explicitUrl;
  }

  const baseHost = readConfigValue("NEXT_PUBLIC_sessionmgr_baseHost") || readConfigValue("sessionmgr_baseHost");
  if (baseHost) {
    const proto =
      readConfigValue("NEXT_PUBLIC_sessionmgr_baseurl_proto") || readConfigValue("sessionmgr_baseurl_proto") || "https";
    return normalizeUrlLikeValue(`${proto}://${baseHost}`, proto);
  }

  return normalizeUrlLikeValue(readConfigValue("NEXT_PUBLIC_API_URL") || readConfigValue("API_URL"));
};

// Avoid logging runtime config in production; it can leak values into logs.
// Also avoid noisy logs in tests.
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test") {
  // eslint-disable-next-line no-console
  console.log("[Config] Initializing with window.__RUNTIME_CONFIG__:", window.__RUNTIME_CONFIG__);
}

export const API_URL = normalizeUrlLikeValue(readConfigValue("NEXT_PUBLIC_API_URL") || process.env.NEXT_PUBLIC_API_URL);
export const COMPANY_API_URL = normalizeUrlLikeValue(
  readConfigValue("NEXT_PUBLIC_COMPANY_API_URL") || process.env.NEXT_PUBLIC_COMPANY_API_URL || API_URL
);
const configuredAppName = getRuntimeConfig("NEXT_PUBLIC_APP_NAME") || process.env.NEXT_PUBLIC_APP_NAME;
export const APP_NAME =
  configuredAppName?.trim().toLowerCase() === "sellpath" ? "Worksine" : configuredAppName || "Worksine";
export const NEW_PAGE_BASE_64_STRING =
  getRuntimeConfig("NEW_PAGE_BASE_64_STRING") || process.env.NEW_PAGE_BASE_64_STRING;
export const API_BASEURL = getRuntimeConfig("NEXT_PUBLIC_API_BASEURL") || process.env.NEXT_PUBLIC_API_BASEURL;
export const WEBUI_URL = getRuntimeConfig("NEXT_PUBLIC_WEBUI_URL") || process.env.NEXT_PUBLIC_WEBUI_URL;
export const SHOW_URL = getRuntimeConfig("NEXT_PUBLIC_SHOW_URL") || process.env.NEXT_PUBLIC_SHOW_URL;
export const RECAPTCHA_SITE_KEY =
  getRuntimeConfig("NEXT_PUBLIC_RECAPTCHA_SITE_KEY") || process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";
export const SESSIONMGR_URL = resolveSessionmgrUrl();
export const WEBSOCKET_URL = getRuntimeConfig("NEXT_PUBLIC_WEBSOCKET_URL") || process.env.NEXT_PUBLIC_WEBSOCKET_URL;
export const CHAT_PLACEHOLDER =
  getRuntimeConfig("NEXT_PUBLIC_CHAT_PLACEHOLDER") || process.env.NEXT_PUBLIC_CHAT_PLACEHOLDER;
// Hard-disabled for now. Re-enable only after the onboarding-tour rollout is fully reworked.
export const ENABLE_ONBOARDING_TOURS = false;
export const ENABLE_PRIVILEGED_RUNTIME_ACTIONS =
  getRuntimeConfig("NEXT_PUBLIC_ENABLE_PRIVILEGED_RUNTIME_ACTIONS") ||
  process.env.NEXT_PUBLIC_ENABLE_PRIVILEGED_RUNTIME_ACTIONS;
export const ENABLE_EXPERIMENTAL_TRIAGE_AGENT_PROVISIONING_MUTATIONS =
  getRuntimeConfig("NEXT_PUBLIC_ENABLE_EXPERIMENTAL_TRIAGE_AGENT_PROVISIONING_MUTATIONS") ||
  process.env.NEXT_PUBLIC_ENABLE_EXPERIMENTAL_TRIAGE_AGENT_PROVISIONING_MUTATIONS;
export const NOVU_BACKENDURL =
  getRuntimeConfig("NEXT_PUBLIC_NOVU_BACKENDURL") || process.env.NEXT_PUBLIC_NOVU_BACKENDURL;
export const NOVU_SOCKETURL = getRuntimeConfig("NEXT_PUBLIC_NOVU_SOCKETURL") || process.env.NEXT_PUBLIC_NOVU_SOCKETURL;
export const NOVU_APPLICATIONIDENTIFIER =
  getRuntimeConfig("NEXT_PUBLIC_NOVU_APPLICATIONIDENTIFIER") || process.env.NEXT_PUBLIC_NOVU_APPLICATIONIDENTIFIER;

// Dogfooding Chat Widget Config
export const DISABLE_CHAT_WIDGET =
  getRuntimeConfig("NEXT_PUBLIC_DISABLE_CHAT_WIDGET") === "true" ||
  process.env.NEXT_PUBLIC_DISABLE_CHAT_WIDGET === "true";
export const CHAT_WIDGET_SCRIPT_URL =
  getRuntimeConfig("NEXT_PUBLIC_CHAT_WIDGET_SCRIPT_URL") ||
  process.env.NEXT_PUBLIC_CHAT_WIDGET_SCRIPT_URL ||
  "http://localhost:8080/sess/v1/widget/static-widget/widget.js";
export const CHAT_WIDGET_API_URL =
  getRuntimeConfig("NEXT_PUBLIC_CHAT_WIDGET_API_URL") ||
  process.env.NEXT_PUBLIC_CHAT_WIDGET_API_URL ||
  "http://localhost:8080/sess/v1/widget";
export const CHAT_WIDGET_KEY =
  getRuntimeConfig("NEXT_PUBLIC_CHAT_WIDGET_KEY") || process.env.NEXT_PUBLIC_CHAT_WIDGET_KEY || "ops.sellpath.ai";
export const AUTH_MARKET = getRuntimeConfig("NEXT_PUBLIC_AUTH_MARKET") || process.env.NEXT_PUBLIC_AUTH_MARKET;
export const AUTH_TERMS_URL = getRuntimeConfig("NEXT_PUBLIC_AUTH_TERMS_URL") || process.env.NEXT_PUBLIC_AUTH_TERMS_URL;
export const AUTH_PRIVACY_URL =
  getRuntimeConfig("NEXT_PUBLIC_AUTH_PRIVACY_URL") || process.env.NEXT_PUBLIC_AUTH_PRIVACY_URL;
export const AUTH_SUPPORT_URL =
  getRuntimeConfig("NEXT_PUBLIC_AUTH_SUPPORT_URL") || process.env.NEXT_PUBLIC_AUTH_SUPPORT_URL;
export const AUTH_SUPPORT_EMAIL =
  getRuntimeConfig("NEXT_PUBLIC_AUTH_SUPPORT_EMAIL") || process.env.NEXT_PUBLIC_AUTH_SUPPORT_EMAIL;
export const SIGNUP_EMAIL_VERIFICATION_ENABLED =
  getRuntimeConfig("NEXT_PUBLIC_SIGNUP_EMAIL_VERIFICATION_ENABLED") !== "false" &&
  process.env.NEXT_PUBLIC_SIGNUP_EMAIL_VERIFICATION_ENABLED !== "false";

// Server-only secrets must never be read from NEXT_PUBLIC_* or exposed via runtime config.
// Use API routes/services to access them.

/**
 * staging
 */
export const JWT = "st-access-token";
export const SPJWT = "st-access-token";
export const SPTENANTID = "sp-tenant-id";
export const SPUSERGUID = "sp-user-guid";
export const REFRESHTOKEN = "st-refresh-token";
export const LOGIN_USER_EMAIL = "login-user-email";
export const TOKEN_EXPIRY = "token_expiry";
export const LANG = "lang";

export const OAUTHSTATE = "oauth-state";
export const OAUTHCODE = "oauth-code";
export const SIGNINUPTYPE = "signinup-type";
export const OAUTHPENDINGSIGNIN = "oauth-pending-signin";
export const SIGNUP_BACK_TO = "signup-back-to";
export const OAUTHCONFIRMEDSIGNUP = "oauth-confirmed-signup";
export const OAUTH_LINKING = "oauth-linking";
export const GOOGLE_ID_TOKEN = "google_id_token";
export const GOOGLE_EMAIL = "google_email";

export const API_CODE = {
  SUCCESS: "SUCCESS",
  SERVER_MAINTENANCE: "SERVER_MAINTENANCE",
  AUTHENTICATION_INVALID: "AUTHENTICATION_INVALID",
  NOT_FOUND: "NOT_FOUND",
  FORBIDDEN: "FORBIDDEN"
};

export const firebaseConfig = {
  apiKey: "",
  authDomain: "",
  projectId: "",
  storageBucket: "",
  messagingSenderId: "",
  appId: "",
  measurementId: ""
};

export const ROLES = {
  ADMIN: "Admin",
  USER: "User",
  MANAGER: "Manager",
  SALES: "Sales Role"
};

export const MODEL_TYPE_SEARCH = {
  SALES_ACCOUNT: "SalesAccount",
  SALES_CUSTOMER: "SalesCustomer",
  SALES_ACTIVITY: "SalesActivity",
  SALES_CAMPAIGN: "SalesCampaign",
  SALES_AGENT: "SalesAIAgent",
  SALES_PRODUCT: "SalesProduct",
  SALES_MEMBER: "SalesMember",
  SALES_LEAD: "SalesLead",
  SALES_ESCALATION: "SalesEscalation",
  TASKS: "Tasks",
  PERMISSION: "Permission",
  GROUP: "Group",
  APP_INTEGRATIONS: "App_integrations",
  APP_PAGES: "AppPages",
  PROMPT: "Prompt",
  CHAIN: "Chain",
  TOPIC: "Topic"
};
